import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import {
  backgroundColor,
  color,
  display,
  fontSize,
  fontWeight,
  lineHeight,
  overflow,
  position,
  space,
} from 'styled-system';

import Badge from 'common/components/Badge/CountBadge';
import DefaultProductImage from 'common/components/DefaultProductImage';
import { Flex } from 'common/components/Flex';
import type { CardImageProps } from 'common/components/Reclamation/CardImage';
import CardImage from 'common/components/Reclamation/CardImage';
import { useLocalization } from 'common/providers/LocaleProvider';
import { themeRight, themeTop } from 'common/theme/system-utilities';
import type { ImageSizes } from 'common/types/Image';

interface DrawerFormHeadlineProps {
  claimedQuantity: number;
  src: Nullable<string>;
  title: CardImageProps['alt'];
  subtitle: string;
  shopName?: string;
}

const ITEM_QUANTITY_POSITION = '-8px';

const IMG_SIZE_THUMBNAIL = 24;
const IMG_SIZE_S_M = 56;
const IMG_SIZE_L = 64;
const IMG_SIZE_XL = 70;

const IMAGE_SIZES: ImageSizes = {
  thumbnail: IMG_SIZE_THUMBNAIL,
  mobile: IMG_SIZE_S_M,
  tablet: IMG_SIZE_S_M,
  desktop: IMG_SIZE_L,
  large_desktop: IMG_SIZE_XL,
};

const responsiveImageSizes = [IMG_SIZE_S_M, null, IMG_SIZE_L, IMG_SIZE_XL];

const UnstyledCard = styled(Flex)<any>`
  ${backgroundColor};
  ${space};
`;

const Card = (props: ComponentProps<typeof UnstyledCard>) => (
  <UnstyledCard
    as="article"
    alignItems="center"
    backgroundColor="sys.neutral.background.weak"
    pl="sp_20"
    pr="sp_16"
    py={['sp_20', null, 'sp_16']}
    {...props}
  />
);

const imageProps = {
  width: responsiveImageSizes,
  minWidth: responsiveImageSizes,
  height: responsiveImageSizes,
  minHeight: responsiveImageSizes,
};

const UnstyledImageWrapper = styled(Flex)<any>`
  ${position};
`;

const ImageWrapper = (props: ComponentProps<typeof UnstyledImageWrapper>) => (
  <UnstyledImageWrapper position="relative" {...props} />
);

const UnstyledStyledImage = styled(CardImage)<any>``;

const StyledImage = (props: ComponentProps<typeof UnstyledStyledImage>) => (
  <UnstyledStyledImage {...imageProps} {...props} />
);

const UnstyledQuantity = styled(Badge)<any>`
  ${position};
  ${themeTop};
  ${themeRight};
`;

const Quantity = (props: ComponentProps<typeof UnstyledQuantity>) => (
  <UnstyledQuantity
    position="absolute"
    themeTop={ITEM_QUANTITY_POSITION}
    themeRight={ITEM_QUANTITY_POSITION}
    {...props}
  />
);

const UnstyledDefaultImage = styled(DefaultProductImage)<any>``;

const StyledDefaultImage = (props: ComponentProps<typeof UnstyledDefaultImage>) => (
  <UnstyledDefaultImage {...imageProps} {...props} />
);

const UnstyledTitleWrapper = styled.div<any>`
  ${color};
  ${space};
  ${overflow};
`;

const TitleWrapper = (props: ComponentProps<typeof UnstyledTitleWrapper>) => (
  <UnstyledTitleWrapper color="sys.neutral.text.strong" pl="sp_8" overflow="hidden" {...props} />
);

const UnstyledTitle = styled.span<any>`
  ${display};
  ${fontSize};
  ${fontWeight};
  ${lineHeight};
  ${overflow};

  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Title = (props: ComponentProps<typeof UnstyledTitle>) => (
  <UnstyledTitle
    display="block"
    fontSize={['fs_16', null, 'fs_18', 'fs_20']}
    fontWeight="bold"
    lineHeight={['lh_20', null, 'lh_22', 'lh_24']}
    overflow="hidden"
    {...props}
  />
);

const UnstyledSubtitle = styled.span<any>`
  ${display};
  ${fontSize};
  ${lineHeight};
  ${overflow};

  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Subtitle = (props: ComponentProps<typeof UnstyledSubtitle>) => (
  <UnstyledSubtitle
    display="block"
    fontSize={['fs_14', null, 'fs_16', 'fs_18']}
    lineHeight={['lh_16', null, 'lh_20', 'lh_22']}
    overflow="hidden"
    {...props}
  />
);

const DrawerFormHeadline = ({
  src,
  title,
  subtitle,
  shopName,
  claimedQuantity,
}: DrawerFormHeadlineProps) => {
  const { t } = useLocalization();

  return (
    <Card>
      {src ? (
        <ImageWrapper>
          <StyledImage
            aria-hidden="true"
            tabIndex="-1"
            src={src}
            title={title}
            imageSizes={IMAGE_SIZES}
          />
          {claimedQuantity > 1 && <Quantity>{claimedQuantity}</Quantity>}
        </ImageWrapper>
      ) : (
        <StyledDefaultImage title={title} />
      )}
      <TitleWrapper data-testid="reclamation-drawer-form-headline-title-wrapper">
        <Title data-testid="reclamation-drawer-form-headline-title">{title}</Title>
        <Subtitle data-testid="reclamation-drawer-form-headline-subtitle">{subtitle}</Subtitle>
        {shopName && (
          <Subtitle data-testid="reclamation-drawer-form-headline-shop-name">
            {t('h24_seller_label')}: {shopName}
          </Subtitle>
        )}
      </TitleWrapper>
    </Card>
  );
};

export default DrawerFormHeadline;
